import { createWebHistory, createRouter } from "vue-router";
import pinia, { useAuthStore } from "@/state/pinia";
import routes from "./routes";

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    return { x: 0, y: 0 };
  },
});

const PUBLIC_PREFIXES = ["/login", "/logout", "/auth/callback"];

// Ścieżki na których onboarding guard NIE działa — tu user może wylądować
// gdy nie ma jeszcze workspace (sam onboarding flow).
const ONBOARDING_PATHS = [
  "/workspaces/new",
  "/workspaces",
];

function isPublic(path) {
  return PUBLIC_PREFIXES.some((p) => path.startsWith(p));
}

function isOnboarding(path) {
  return ONBOARDING_PATHS.some((p) => path === p || path.startsWith(p + "/"));
}

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore(pinia);

  if (!auth.token && auth.loadToken) {
    auth.loadToken();
  }

  const token = auth.getToken && auth.getToken();
  const publicRoute = isPublic(to.path);
  const ssoOn = auth.isSsoEnabled && auth.isSsoEnabled();

  // SSO mode: NIGDY nie pokazuj lokalnego formularza logowania. Wejście na /login
  // bez tokena → od razu redirect na SSO authorize. (/auth/callback zostaje publiczny,
  // żeby dokończyć PKCE.) Lokalny /login działa tylko gdy SSO nie jest skonfigurowane.
  if (ssoOn && !token && to.path.startsWith("/login")) {
    auth.redirectToSso();
    return;
  }

  // 1. Bez tokena → SSO (jeśli skonfigurowane), inaczej lokalny login
  if (!publicRoute && !token) {
    if (ssoOn) {
      auth.redirectToSso();
      return;
    }
    return next({ name: "login" });
  }

  // 2. Zalogowany na /login → wyrzuć na dashboard
  if (to.name === "login" && token) {
    return next({ name: "default" });
  }

  // 3. Onboarding guard — wymaga workspace.
  //    Pomijamy gdy user jest już na ścieżce onboardingu lub logout.
  if (token && !publicRoute && !isOnboarding(to.path) && to.path !== "/logout") {
    let user = auth.getUser?.() || auth.user;
    let hasWorkspace = Object.keys(user?.workspaces || {}).length > 0;

    // Cached user may be stale (e.g. workspace added in SSO after login, or a
    // session created before workspace-sync existed). Before forcing onboarding,
    // re-fetch /me which live-syncs workspaces from SSO, then re-check.
    if (!hasWorkspace) {
      await auth.refreshMe();
      user = auth.getUser?.() || auth.user;
      hasWorkspace = Object.keys(user?.workspaces || {}).length > 0;
    }

    if (!hasWorkspace) {
      return next({ name: "workspaces.create" });
    }
  }

  next();
});

router.afterEach((to) => {
  document.title = `${to.meta.title || "LessServices"} | LessServices`;
});

export default router;
